import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { cn } from "@/lib/utils";
import { isValidPassword, PASSWORD_POLICY_MESSAGE } from "@/lib/passwordPolicy";
import { usePasswordPolicyHint } from "@/hooks/usePasswordPolicyHint";

export function ChangePasswordSection() {
  const passwordHint = usePasswordPolicyHint();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const newPasswordValid = isValidPassword(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  const newPasswordDifferent = !currentPassword || newPassword !== currentPassword;
  const canChangePassword = !!currentPassword
    && newPasswordValid
    && !!confirmPassword
    && passwordsMatch
    && newPasswordDifferent;

  const onChangePassword = async () => {
    if (!currentPassword) {
      toast.error("Enter your current password");
      return;
    }
    if (!newPasswordValid) {
      toast.error(passwordHint || PASSWORD_POLICY_MESSAGE);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation password do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("New password must be different from your current password.");
      return;
    }
    setChangingPassword(true);
    try {
      const { authApi } = await import("@/lib/api");
      await authApi.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed successfully.");
    } catch (e) {
      if (e?.code === "invalid_password") {
        toast.error(e?.message || "Current password is incorrect.");
      } else if (e?.code === "weak_password") {
        toast.error(e?.message || PASSWORD_POLICY_MESSAGE);
      } else if (e?.code === "same_password") {
        toast.error(e?.message || "New password must be different from your current password.");
      } else {
        toast.error(e?.message || "Failed to change password");
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5"
      data-testid="change-password-section"
    >
      <div className="text-[14px] font-semibold text-slate-900">
        Change Password
      </div>
      <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
        Update your account password. You will stay signed in on this device.
      </p>
      <div className="mt-4 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="current-password" className="text-[12px] text-slate-700">
            Current Password
          </Label>
          <PasswordInput
            id="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="Enter your current password"
            data-testid="change-password-current"
            toggleTestId="change-password-current-toggle"
            className="h-10"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new-password" className="text-[12px] text-slate-700">
            New Password
          </Label>
          <PasswordInput
            id="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="Enter your new password"
            data-testid="change-password-new"
            toggleTestId="change-password-new-toggle"
            className="h-10"
            minLength={10}
          />
          {passwordHint ? (
            <p className="text-[11px] text-slate-500">{passwordHint}</p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm-password" className="text-[12px] text-slate-700">
            Confirm New Password
          </Label>
          <PasswordInput
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="Confirm your new password"
            data-testid="change-password-confirm"
            toggleTestId="change-password-confirm-toggle"
            className={cn(
              "h-10",
              confirmPassword && !passwordsMatch && "border-rose-300 focus-visible:ring-rose-200",
            )}
            minLength={10}
          />
          {confirmPassword && !passwordsMatch ? (
            <p className="text-[11.5px] text-rose-600" data-testid="change-password-mismatch">
              New password and confirmation password do not match.
            </p>
          ) : null}
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          disabled={changingPassword || !canChangePassword}
          onClick={onChangePassword}
          data-testid="change-password-submit"
        >
          {changingPassword ? "Changing…" : "Change Password"}
        </Button>
      </div>
    </section>
  );
}
